import {
    type DashboardSummary,
    getDashboardPreview
} from '@/lib/dashboards-api';
import { relativeTime } from '@/lib/relativeTime';
import { cn } from '@/lib/utils';
import { Info, Lock, Settings } from 'lucide-react';
import useSWR from 'swr';

import DashboardPreview from '@/components/chat/MessageComposer/DashboardPreview';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

function LockedPreview({ dashboardId }: { dashboardId: string }) {
    const { data } = useSWR(
        ['dashboard-preview', dashboardId],
        ([, id]) => getDashboardPreview(id),
        { revalidateOnFocus: false, shouldRetryOnError: false }
    );

    return (
        <div className="relative size-full bg-muted/40 bg-[radial-gradient(hsl(var(--border))_1px,transparent_1px)] bg-[length:14px_14px]">
            {data ? (
                <div className="opacity-70 blur-[3px] [&>div]:shadow-none">
                    <DashboardPreview
                        dashboardId={dashboardId}
                        html={data.html}
                    />
                </div>
            ) : (
                <div className="size-full" />
            )}
            <div className="absolute inset-0 flex justify-center items-center">
                <span className="flex items-center gap-1.5 bg-background shadow-xs px-3 py-1.5 border border-border rounded-full text-muted-foreground text-subtitle">
                    <Lock className="size-3.5" strokeWidth={1.5} />
                    No access
                </span>
            </div>
        </div>
    );
}

const SKELETON_CARD_COUNT = 6;

export function ArtifactCardsSkeleton() {
    return (
        <div className="@container" aria-busy="true" aria-label="Loading artifacts">
            <div className="gap-3 grid grid-cols-1 sm:grid-cols-2 @4xl:grid-cols-3">
                {Array.from({ length: SKELETON_CARD_COUNT }, (_, index) => (
                    <div
                        key={index}
                        className="flex flex-col border border-border rounded-md overflow-hidden"
                    >
                        <Skeleton className="border-border border-b rounded-none aspect-video" />
                        <div className="flex flex-col gap-2 p-4">
                            <Skeleton className="w-3/4 h-4" />
                            <Skeleton className="w-1/3 h-3" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

interface ArtifactCardsProps {
    dashboards: DashboardSummary[];
    selectedId: string | null;
    onSelect: (id: string) => void;
    onOpen?: (id: string) => void;
    canView?: (dashboard: DashboardSummary) => boolean;
    ownerOf?: (dashboard: DashboardSummary) => string | undefined;
    canManage?: (dashboard: DashboardSummary) => boolean;
    onManage?: (dashboard: DashboardSummary) => void;
}

export function ArtifactCards({
    dashboards,
    selectedId,
    onSelect,
    onOpen,
    canView,
    ownerOf,
    canManage,
    onManage
}: ArtifactCardsProps) {
    return (
        <div className="@container">
            <div className="gap-3 grid grid-cols-1 sm:grid-cols-2 @4xl:grid-cols-3">
                {dashboards.map((dashboard) => {
                    const owner = ownerOf?.(dashboard);
                    const viewable = canView?.(dashboard) ?? true;
                    const openCard = viewable ? (onOpen ?? onSelect) : onSelect;
                    const manageable = Boolean(
                        onManage && canManage?.(dashboard)
                    );
                    const selected = dashboard.id === selectedId;
                    return (
                        <div
                            key={dashboard.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => openCard(dashboard.id)}
                            onKeyDown={(event) => {
                                if (event.target !== event.currentTarget)
                                    return;
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    openCard(dashboard.id);
                                }
                            }}
                            className={cn(
                                'flex flex-col bg-background shadow-xs hover:shadow-md border border-border rounded-md overflow-hidden text-left cursor-pointer outline-none focus-visible:ring-1 focus-visible:ring-focus-ring transition hover:-translate-y-1 transform-gpu',
                                selected && 'bg-hover'
                            )}
                        >
                            <div className="relative border-b border-border aspect-video overflow-hidden [&>div]:shadow-none [&>div]:border-0 [&>div]:rounded-none">
                                {viewable ? (
                                    <DashboardPreview dashboardId={dashboard.id} />
                                ) : (
                                    <LockedPreview dashboardId={dashboard.id} />
                                )}
                            </div>
                            <div className="flex flex-1 justify-between items-center gap-3 p-4">
                                <div className="flex flex-col min-w-0">
                                    <span className="font-medium text-title line-clamp-2">
                                        {dashboard.title}
                                    </span>
                                    <span className="text-body text-muted-foreground truncate">
                                        {owner ??
                                            relativeTime(dashboard.updatedAt)}
                                    </span>
                                </div>
                                <div className="z-50 relative flex items-center gap-1.5 shrink-0">
                                    {onOpen && (
                                        <Button
                                            variant="tertiary"
                                            size="icon"
                                            aria-label="Details"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                onSelect(dashboard.id);
                                            }}
                                        >
                                            <Info />
                                        </Button>
                                    )}
                                    {manageable && (
                                        <Button
                                            variant="tertiary"
                                            size="icon"
                                            aria-label="Manage access"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                onManage?.(dashboard);
                                            }}
                                        >
                                            <Settings />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
